import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MapPin, Heart, SlidersHorizontal, X, ChevronLeft, ChevronRight,
  Fuel, Gauge, Calendar, Settings2, Navigation, Phone, Globe,
  GitCompareArrows, ArrowUpDown, Search as SearchIcon, MessageCircle, Car
} from 'lucide-react';
import ChatPanel from '../components/search/ChatPanel';

const API = window.location.origin;

const fmt = {
  price: (p) => p ? `${p.toLocaleString('fr-BE')} €` : '',
  km: (k) => k ? `${k.toLocaleString('fr-BE')} km` : '',
  dist: (d) => d != null ? `${d} km` : '',
};

export default function Search() {
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [liked, setLiked] = useState([]);
  const [passed, setPassed] = useState([]);
  const [location, setLocation] = useState({ city: '', lat: null, lng: null, radius: 50 });
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [focusVehicle, setFocusVehicle] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [mobileTab, setMobileTab] = useState('vehicles'); // 'chat' | 'vehicles'
  const [currentIdx, setCurrentIdx] = useState(0);
  const geoLoaded = useRef(false);

  // Geolocate on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/geolocate`);
        const data = await res.json();
        setLocation({ city: data.city, lat: data.lat, lng: data.lng, radius: 50 });
        geoLoaded.current = true;
      } catch {
        geoLoaded.current = true;
      }
      try {
        const res = await fetch(`${API}/api/public/vehicles/makes`);
        const data = await res.json();
        setMakes(data.makes || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!geoLoaded.current) return;
    loadVehicles();
  }, [filters, page, location]);

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (location.lat) { params.set('lat', location.lat); params.set('lng', location.lng); params.set('radius', location.radius); }
      params.set('page', page);
      params.set('limit', 20);
      params.set('sort', location.lat ? 'nearest' : 'newest');
      const res = await fetch(`${API}/api/public/vehicles/search?${params}`);
      const data = await res.json();
      setVehicles(data.vehicles || []);
      setTotal(data.total || 0);
    } catch { setVehicles([]); }
    finally { setLoading(false); }
  }, [filters, page, location]);

  const toggleLike = (id) => {
    if (liked.includes(id)) {
      setLiked(liked.filter(l => l !== id));
    } else {
      setLiked([...liked, id]);
      setPassed(passed.filter(p => p !== id));
    }
  };

  const doPass = (id) => {
    if (!passed.includes(id)) setPassed([...passed, id]);
    setLiked(liked.filter(l => l !== id));
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }));
    setPage(1);
  };

  const handleChatVehicles = (chatVehicles) => {
    setVehicles(prev => {
      const existingIds = new Set(prev.map(v => v.id));
      const newVehicles = chatVehicles.filter(v => !existingIds.has(v.id));
      return [...newVehicles, ...prev];
    });
  };

  const totalPages = Math.ceil(total / 20) || 1;

  // WebMCP — register tools for AI agents
  useEffect(() => {
    if (!navigator.modelContext) return;
    const tools = [
      {
        name: "search_all_vehicles",
        description: "Search vehicles across ALL dealerships on the Carmin platform in Belgium. Returns matching vehicles with dealer info, price, mileage, and distance.",
        inputSchema: {
          type: "object",
          properties: {
            make: { type: "string", description: "Car brand (e.g. BMW, Ford, Toyota)" },
            model: { type: "string", description: "Car model (e.g. Golf, Serie 3)" },
            fuel_type: { type: "string", enum: ["Essence", "Diesel", "Électrique", "Hybride", "CNG", "LPG"] },
            body_type: { type: "string", description: "Body type (SUV, Berline, Break, Monospace, Coupé)" },
            transmission: { type: "string", enum: ["Automatique", "Manuel"] },
            min_price: { type: "number", description: "Minimum price in EUR" },
            max_price: { type: "number", description: "Maximum price in EUR" },
            min_year: { type: "integer" }, max_year: { type: "integer" },
            max_mileage: { type: "integer", description: "Maximum mileage in km" },
            sort: { type: "string", enum: ["nearest", "newest", "price_asc", "price_desc", "mileage"] },
            limit: { type: "integer", description: "Number of results (default 20, max 50)" },
          },
        },
        annotations: { readOnlyHint: true },
        execute: async (input) => {
          const params = new URLSearchParams();
          Object.entries(input).forEach(([k, v]) => { if (v != null) params.set(k, v); });
          if (location.lat) { params.set("lat", location.lat); params.set("lng", location.lng); params.set("radius", location.radius); }
          const res = await fetch(`${API}/api/public/vehicles/search?${params}`);
          const data = await res.json();
          return { total: data.total, vehicles: data.vehicles?.map(v => ({
            id: v.id, make: v.make, model: v.model, year: v.year, mileage: v.mileage,
            price: v.price, fuel_type: v.fuel_type, distance_km: v.distance_km,
            dealer_name: v.dealer?.name, dealer_phone: v.dealer?.phone,
          })) };
        },
      },
      {
        name: "get_vehicle_details",
        description: "Get full details of a specific vehicle including specs, equipment, photos, price, and dealer contact.",
        inputSchema: { type: "object", properties: { vehicle_id: { type: "string" } }, required: ["vehicle_id"] },
        annotations: { readOnlyHint: true },
        execute: async (input) => {
          const res = await fetch(`${API}/api/public/vehicles/detail/${input.vehicle_id}`);
          return await res.json();
        },
      },
      {
        name: "get_available_makes",
        description: "List all car brands available on the Carmin platform with count per brand.",
        inputSchema: { type: "object", properties: {} },
        annotations: { readOnlyHint: true },
        execute: async () => {
          const res = await fetch(`${API}/api/public/vehicles/makes`);
          return await res.json();
        },
      },
    ];
    tools.forEach(t => { try { navigator.modelContext.registerTool(t); } catch {} });
    return () => { tools.forEach(t => { try { navigator.modelContext.unregisterTool(t.name); } catch {} }); };
  }, [location]);

  return (
    <div className="h-screen bg-dark-bg flex flex-col overflow-hidden">
      {/* Top bar */}
      <header className="bg-dark-surface border-b border-dark-border px-4 py-3 flex items-center justify-between shrink-0 z-30">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold tracking-tight">
            <span className="text-carmin">Carmin</span>
            <span className="text-muted font-normal ml-1.5 text-sm">Match</span>
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Location */}
          <button
            onClick={() => setShowLocationPicker(!showLocationPicker)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-dark-card border border-dark-border text-xs text-secondary hover:border-gray-600 hover:text-primary transition-colors"
          >
            <MapPin size={13} className="text-carmin" />
            {location.city ? `${location.city} · ${location.radius} km` : 'Localisation...'}
          </button>

          {/* Liked count */}
          {liked.length > 0 && (
            <button
              onClick={() => setShowComparison(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-carmin-50 border border-carmin/30 text-xs font-semibold text-carmin-light hover:bg-carmin/20 transition-colors"
            >
              <Heart size={13} className="fill-current" />
              {liked.length} favoris
            </button>
          )}

          <span className="text-xs text-dimmed hidden sm:inline">{total} véhicules</span>
        </div>
      </header>

      {/* Location picker dropdown */}
      {showLocationPicker && (
        <div className="bg-dark-card border-b border-dark-border px-4 py-3 flex flex-wrap items-center gap-3 z-20">
          <input
            placeholder="Ville ou code postal"
            defaultValue={location.city}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const city = e.target.value;
                fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city + ', Belgium')}&format=json&limit=1`)
                  .then(r => r.json()).then(data => {
                    if (data[0]) setLocation(prev => ({ ...prev, city, lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }));
                  });
              }
            }}
            className="flex-1 min-w-[180px] bg-dark-input border border-dark-border rounded-lg px-3 py-2 text-sm text-primary placeholder-muted outline-none focus:ring-2 focus:ring-carmin/50 focus:border-carmin/50"
          />
          <div className="flex gap-1.5">
            {[25, 50, 100, 150].map(r => (
              <button
                key={r}
                onClick={() => setLocation(prev => ({ ...prev, radius: r }))}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  location.radius === r
                    ? 'bg-carmin text-white'
                    : 'bg-dark-hover border border-dark-border text-secondary hover:text-primary'
                }`}
              >
                {r} km
              </button>
            ))}
          </div>
          <button onClick={() => setShowLocationPicker(false)} className="text-muted hover:text-primary transition-colors">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Mobile tabs */}
      <div className="flex lg:hidden border-b border-dark-border shrink-0">
        <button
          onClick={() => setMobileTab('chat')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            mobileTab === 'chat'
              ? 'text-accent-purple border-b-2 border-accent-purple'
              : 'text-muted hover:text-secondary'
          }`}
        >
          <MessageCircle size={16} />
          Conversation
        </button>
        <button
          onClick={() => setMobileTab('vehicles')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
            mobileTab === 'vehicles'
              ? 'text-carmin border-b-2 border-carmin'
              : 'text-muted hover:text-secondary'
          }`}
        >
          <Car size={16} />
          Véhicules
          {total > 0 && <span className="text-xs text-dimmed">({total})</span>}
        </button>
      </div>

      {/* Main split layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel — Conversation */}
        <div className={`w-full lg:w-[40%] xl:w-[35%] bg-dark-surface border-r border-dark-border flex flex-col ${
          mobileTab !== 'chat' ? 'hidden lg:flex' : 'flex'
        }`}>
          <ChatPanel
            location={location}
            liked={liked}
            passed={passed}
            onVehiclesUpdate={handleChatVehicles}
          />
        </div>

        {/* Right panel — Vehicle grid */}
        <div className={`w-full lg:w-[60%] xl:w-[65%] flex flex-col overflow-hidden ${
          mobileTab !== 'vehicles' ? 'hidden lg:flex' : 'flex'
        }`}>
          {/* Vehicle discovery — Tinder-style */}
          <VehicleDiscovery
            vehicles={vehicles}
            loading={loading}
            liked={liked}
            passed={passed}
            currentIdx={currentIdx}
            total={total}
            makes={makes}
            filters={filters}
            onLike={(id) => { toggleLike(id); setCurrentIdx(i => i + 1); }}
            onPass={(id) => { doPass(id); setCurrentIdx(i => i + 1); }}
            onSelect={(v) => setFocusVehicle(v)}
            onFilterChange={updateFilter}
            onIdxChange={setCurrentIdx}
          />
        </div>
      </div>

      {/* Focus view overlay */}
      {focusVehicle && (
        <FocusView
          vehicle={focusVehicle}
          liked={liked.includes(focusVehicle.id)}
          onLike={() => toggleLike(focusVehicle.id)}
          onPass={() => { doPass(focusVehicle.id); setFocusVehicle(null); }}
          onClose={() => setFocusVehicle(null)}
        />
      )}

      {/* Comparison overlay */}
      {showComparison && liked.length >= 2 && (
        <ComparisonTable
          vehicles={vehicles.filter(v => liked.includes(v.id))}
          liked={liked}
          onRemove={(id) => setLiked(liked.filter(l => l !== id))}
          onClose={() => setShowComparison(false)}
        />
      )}
    </div>
  );
}

/* ---- Vehicle Discovery (Bumble stacked cards) ---- */
function VehicleDiscovery({ vehicles, loading, liked, passed, currentIdx, total, makes, filters, onLike, onPass, onSelect, onFilterChange, onIdxChange }) {
  const visible = vehicles.filter(v => !passed.includes(v.id));
  const stack = visible.slice(currentIdx, currentIdx + 3); // current + 2 behind

  return (
    <div className="flex flex-col h-full">
      {/* Filters — pill chips */}
      <div className="px-4 py-2.5 border-b border-dark-border flex items-center gap-2 overflow-x-auto scrollbar-hide shrink-0">
        <SlidersHorizontal size={13} className="text-muted shrink-0" />
        {[
          { key: 'make', label: 'Marque', opts: makes.map(m => ({ v: m.name, l: `${m.name} (${m.count})` })) },
          { key: 'fuel_type', label: 'Carburant', opts: ['Essence', 'Diesel', 'Électrique', 'Hybride'].map(f => ({ v: f, l: f })) },
          { key: 'transmission', label: 'Boîte', opts: [{ v: 'Automatique', l: 'Auto' }, { v: 'Manuel', l: 'Manuel' }] },
          { key: 'body_type', label: 'Type', opts: ['SUV', 'Berline', 'Break', 'Monospace', 'Coupé'].map(t => ({ v: t, l: t })) },
          { key: 'max_price', label: 'Budget', opts: [5000, 10000, 15000, 20000, 30000, 50000].map(p => ({ v: String(p), l: `≤ ${p.toLocaleString('fr-BE')} €` })) },
        ].map(f => (
          <select key={f.key} onChange={e => onFilterChange(f.key, e.target.value)}
            className="bg-dark-input border border-dark-border rounded-lg px-2 py-1.5 text-xs text-secondary outline-none focus:border-carmin/50 cursor-pointer shrink-0">
            <option value="">{f.label}</option>
            {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
      </div>

      {/* Card stack area */}
      <div className="flex-1 flex flex-col items-center justify-center p-5 pt-10 overflow-visible">
        {loading ? (
          <div className="w-full">
            <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-dark-hover" />
            </div>
          </div>
        ) : stack.length === 0 ? (
          <div className="text-center px-6">
            <SearchIcon size={44} className="text-dark-border mb-4 mx-auto" />
            <p className="text-secondary text-sm">Plus de véhicules à découvrir</p>
            <p className="text-muted text-xs mt-1.5">Modifiez vos filtres ou parlez au conseiller IA</p>
            {currentIdx > 0 && (
              <button onClick={() => onIdxChange(0)} className="mt-5 px-5 py-2.5 rounded-full bg-dark-card border border-dark-border text-xs text-secondary hover:text-primary transition-colors">
                Revoir depuis le début
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Stacked cards */}
            <div className="relative w-full" style={{ height: 'min(65vh, 500px)' }}>
              {stack.map((v, i) => {
                const isTop = i === 0;
                return (
                  <div
                    key={v.id}
                    className={`absolute left-0 right-0 rounded-2xl overflow-hidden transition-all duration-300 ${
                      isTop
                        ? 'z-30 border border-dark-border shadow-2xl shadow-black/40'
                        : 'border border-white/30 shadow-lg shadow-black/30'
                    }`}
                    style={{
                      top: 0,
                      bottom: 0,
                      transform: `scale(${1 - i * 0.05}) translate(${i * 16}px, ${i * 20}px)`,
                      opacity: 1,
                      background: isTop ? undefined : `rgb(58, 58, 66)`,
                      pointerEvents: isTop ? 'auto' : 'none',
                    }}
                  >
                    {/* Full photo background */}
                    {v.images?.[0] ? (
                      <img
                        src={v.images[0]}
                        alt={`${v.make} ${v.model}`}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        onClick={() => isTop && onSelect(v)}
                      />
                    ) : (
                      <div className="absolute inset-0 bg-dark-hover flex items-center justify-center">
                        <Car size={56} className="text-dark-border" />
                      </div>
                    )}

                    {/* Gradient overlay bottom */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent pointer-events-none" />

                    {/* Distance badge */}
                    {isTop && v.distance_km != null && (
                      <span className="absolute top-4 right-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1.5 backdrop-blur-sm">
                        <Navigation size={11} /> {fmt.dist(v.distance_km)}
                      </span>
                    )}

                    {/* Counter */}
                    {isTop && (
                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className="bg-black/60 text-white/80 text-[11px] px-2.5 py-1 rounded-full backdrop-blur-sm">
                          {currentIdx + 1} / {visible.length}
                        </span>
                        {currentIdx > 0 && (
                          <button onClick={() => onIdxChange(currentIdx - 1)}
                            className="bg-black/60 text-white/80 w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-black/80 transition-colors">
                            <ChevronLeft size={14} />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Info overlay at bottom */}
                    {isTop && (
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h2 className="text-xl font-bold text-white drop-shadow-lg">
                          {v.make} {v.model} {v.version || ''}
                        </h2>
                        <div className="flex items-center gap-3 mt-1.5 text-[12px] text-white/70">
                          <span className="flex items-center gap-1"><Calendar size={12} />{v.year}</span>
                          <span className="flex items-center gap-1"><Gauge size={12} />{fmt.km(v.mileage)}</span>
                          <span className="flex items-center gap-1"><Fuel size={12} />{v.fuel_type}</span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-2xl font-bold text-white drop-shadow-lg">{fmt.price(v.price)}</span>
                          {v.dealer?.name && <span className="text-xs text-white/50">{v.dealer.name}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action buttons — big round Bumble-style */}
            <div className="flex items-center justify-center gap-6 mt-6">
              <button
                onClick={() => onPass(stack[0].id)}
                className="w-16 h-16 rounded-full border-2 border-dark-border bg-dark-card flex items-center justify-center text-secondary hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/10 transition-all active:scale-90"
              >
                <X size={26} />
              </button>
              <button
                onClick={() => stack[0] && onSelect(stack[0])}
                className="w-12 h-12 rounded-full border-2 border-dark-border bg-dark-card flex items-center justify-center text-muted hover:border-accent-purple/50 hover:text-accent-purple hover:bg-accent-purple/10 transition-all active:scale-90"
              >
                <SearchIcon size={20} />
              </button>
              <button
                onClick={() => onLike(stack[0].id)}
                className={`w-16 h-16 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                  liked.includes(stack[0]?.id)
                    ? 'bg-carmin border-2 border-carmin text-white shadow-lg shadow-carmin/30'
                    : 'border-2 border-dark-border bg-dark-card text-secondary hover:border-carmin/50 hover:text-carmin hover:bg-carmin/10'
                }`}
              >
                <Heart size={26} className={liked.includes(stack[0]?.id) ? 'fill-current' : ''} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Focus View ---- */
function FocusView({ vehicle: v, liked, onLike, onPass, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-card rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto border border-dark-border"
        onClick={e => e.stopPropagation()}
      >
        {v.images?.[0] && (
          <img src={v.images[0]} alt="" className="w-full h-56 sm:h-72 object-cover rounded-t-2xl" />
        )}
        <div className="p-5">
          <h2 className="text-xl font-bold text-primary">{v.make} {v.model} {v.version || ''}</h2>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted">
            <span className="flex items-center gap-1"><Calendar size={12} />{v.year}</span>
            <span className="flex items-center gap-1"><Gauge size={12} />{fmt.km(v.mileage)}</span>
            <span className="flex items-center gap-1"><Fuel size={12} />{v.fuel_type}</span>
            <span className="flex items-center gap-1"><Settings2 size={12} />{v.transmission}</span>
          </div>
          <p className="text-2xl font-bold text-carmin mt-3">{fmt.price(v.price)}</p>

          {v.distance_km != null && (
            <p className="text-xs text-secondary mt-2 flex items-center gap-1.5">
              <Navigation size={12} className="text-carmin" />
              À {fmt.dist(v.distance_km)} — {v.dealer?.name}
            </p>
          )}

          {v.equipment?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] font-semibold text-muted uppercase tracking-wider mb-2">Équipements</p>
              <div className="flex flex-wrap gap-1.5">
                {v.equipment.slice(0, 12).map((eq, i) => (
                  <span key={i} className="bg-dark-hover text-secondary text-[11px] px-2.5 py-1 rounded-md border border-dark-border">{eq}</span>
                ))}
                {v.equipment.length > 12 && <span className="text-[11px] text-dimmed px-2 py-1">+{v.equipment.length - 12}</span>}
              </div>
            </div>
          )}

          {v.dealer && (
            <div className="mt-4 bg-dark-hover rounded-xl p-3 border border-dark-border">
              <p className="text-sm font-semibold text-primary">{v.dealer.name}</p>
              {v.dealer.phone && (
                <a href={`tel:${v.dealer.phone}`} className="text-xs text-carmin-light flex items-center gap-1.5 mt-1.5 hover:underline">
                  <Phone size={12} />{v.dealer.phone}
                </a>
              )}
              {v.dealer.website && (
                <a href={v.dealer.website} target="_blank" rel="noreferrer" className="text-xs text-accent-purple-light flex items-center gap-1.5 mt-1 hover:underline">
                  <Globe size={12} />Site web
                </a>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 mt-5">
            <button
              onClick={onPass}
              className="flex-1 py-3 rounded-xl border border-dark-border text-secondary font-semibold text-sm hover:bg-dark-hover hover:text-primary transition-colors"
            >
              Passer
            </button>
            <button
              onClick={onLike}
              className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
                liked
                  ? 'bg-carmin text-white shadow-lg shadow-carmin/20'
                  : 'bg-carmin-50 border border-carmin/40 text-carmin-light hover:bg-carmin hover:text-white'
              }`}
            >
              <Heart size={16} className={liked ? 'fill-current' : ''} />
              {liked ? 'Liké' : 'J\'aime'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- Comparison Table ---- */
function ComparisonTable({ vehicles, liked, onRemove, onClose }) {
  const rows = [
    { label: 'Prix', key: 'price', format: fmt.price, best: 'min' },
    { label: 'Kilomètres', key: 'mileage', format: fmt.km, best: 'min' },
    { label: 'Année', key: 'year', format: v => v, best: 'max' },
    { label: 'Carburant', key: 'fuel_type', format: v => v },
    { label: 'Transmission', key: 'transmission', format: v => v },
    { label: 'Puissance', key: 'power_hp', format: v => v ? `${v} cv` : '-' },
    { label: 'Distance', key: 'distance_km', format: v => v != null ? `${v} km` : '-', best: 'min' },
    { label: 'Équipements', key: 'equipment', format: v => Array.isArray(v) ? v.length : '-', best: 'max' },
    { label: 'Garage', key: 'dealer', format: v => v?.name || '-' },
  ];

  const getBest = (row) => {
    if (!row.best) return null;
    const vals = vehicles.map(v => {
      const val = row.key === 'equipment' ? (v[row.key]?.length || 0) : (v[row.key] ?? null);
      return val;
    }).filter(v => v != null);
    return row.best === 'min' ? Math.min(...vals) : Math.max(...vals);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto border border-dark-border p-5"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-primary flex items-center gap-2">
            <GitCompareArrows size={18} className="text-accent-purple" />
            Comparer ({vehicles.length})
          </h2>
          <button onClick={onClose} className="text-muted hover:text-primary transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 text-muted font-medium text-xs border-b border-dark-border"></th>
                {vehicles.map(v => (
                  <th key={v.id} className="px-3 py-2 text-center border-b border-dark-border min-w-[130px]">
                    <div className="font-bold text-primary text-sm">{v.make} {v.model}</div>
                    <div className="text-[11px] text-muted">{v.version || ''}</div>
                    <button
                      onClick={() => onRemove(v.id)}
                      className="text-[11px] text-carmin hover:text-carmin-light mt-1 transition-colors"
                    >
                      Retirer
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const best = getBest(row);
                return (
                  <tr key={row.key} className="hover:bg-dark-hover/50 transition-colors">
                    <td className="px-3 py-2.5 text-secondary font-medium text-xs border-b border-dark-border/50">{row.label}</td>
                    {vehicles.map(v => {
                      const raw = row.key === 'equipment' ? (v[row.key]?.length || 0) : v[row.key];
                      const isBest = row.best && raw === best;
                      return (
                        <td
                          key={v.id}
                          className={`px-3 py-2.5 text-center border-b border-dark-border/50 text-xs ${
                            isBest ? 'font-bold text-green-400' : 'text-label'
                          }`}
                        >
                          {row.format(v[row.key])}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
